import { useCallback, useState, useRef, useEffect } from "react";
import { getSupabase } from "../utils";

export interface FTSSearchResult {
  id: string;
  relevance: number;
  total_count: number;
  /**
   * The rest of the projected row. `unknown` rather than `any`: the one consumer
   * (ReassignCompanyModal) already type-guards every field it reads
   * (`typeof contact.name === "string"`), so nothing is lost and a future caller
   * cannot silently treat a jsonb column as a string.
   */
  [key: string]: unknown;
}

export interface UseFTSSearchOptions {
  entityType: "contacts" | "companies" | "deals" | "tickets" | "tasks" | "communications" | "offerings";
  limit?: number;
  offset?: number;
  debounceMs?: number;
}

export interface UseFTSSearchReturn {
  search: (query: string) => void;
  isLoading: boolean;
  error: string | null;
  results: FTSSearchResult[];
  totalCount: number;
}

/**
 * Hook for performing full-text search using PostgreSQL FTS
 * Provides relevance-ranked results with optional pagination
 * Includes built-in debouncing to prevent excessive API calls
 * 
 * Usage:
 * const { search, isLoading, results, totalCount } = useFTSSearch({
 *   entityType: 'contacts',
 *   limit: 20,
 *   debounceMs: 300, // Default debounce
 * });
 * 
 * search('john smith'); // Debounced - won't call API until 300ms of inactivity
 */
export function useFTSSearch({
  entityType,
  limit = 1000,
  offset = 0,
  debounceMs = 300,
}: UseFTSSearchOptions): UseFTSSearchReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<FTSSearchResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  // Refs for debouncing
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Identifies the newest run. Every state write is gated on it, so a slow
  // older search can never overwrite (or blank) a newer one's results — the
  // abort signal alone is NOT enough, because a response whose bytes have
  // already arrived still resolves after the abort. Mirrors the same guard in
  // use-fts-search-with-full-rows.ts, which this hook was missing.
  const runIdRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const performSearch = useCallback(
    async (query: string): Promise<void> => {
      const runId = ++runIdRef.current;
      const isCurrent = () => runIdRef.current === runId;

      if (!query || query.trim().length === 0) {
        setResults([]);
        setTotalCount(0);
        setError(null);
        return;
      }

      // Cancel the previous request BEFORE this run starts, and hold the
      // controller in a local so a newer run cannot swap it out from under the
      // fetch below (which would make this run uncancellable).
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setError(null);

      try {
        // Get auth token from Supabase session
        const supabase = getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.access_token) {
          throw new Error("Not authenticated");
        }

        const response = await fetch(`/api/search/${entityType}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            query: query.trim(),
            limit,
            offset,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.statusText}`);
        }

        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.error || "Search failed");
        }

        // A superseded run must not publish its rows. Without this the reassign
        // picker can show the results for a query the user has already replaced.
        if (!isCurrent()) return;

        setResults(data.data || []);
        // Use total_count if available (from RPC), otherwise use count (number of items returned)
        setTotalCount(data.total_count || data.count || 0);
      } catch (err) {
        // Don't set error if request was aborted (user typed again) or if a
        // newer search has already taken over.
        if (err instanceof Error && err.name !== "AbortError" && isCurrent()) {
          const errorMessage = err instanceof Error ? err.message : "Unknown error";
          setError(errorMessage);
          setResults([]);
          setTotalCount(0);
        }
      } finally {
        // Only the newest run owns the spinner; an older run settling must not
        // signal "done" while the current search is still in flight.
        if (isCurrent()) {
          setIsLoading(false);
        }
      }
    },
    [entityType, limit, offset]
  );

  const search = useCallback(
    (query: string): void => {
      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        void performSearch(query);
      }, debounceMs);
    },
    [performSearch, debounceMs]
  );

  return {
    search,
    isLoading,
    error,
    results,
    totalCount,
  };
}
